/**
 * SoulTown Music radio inside the app.
 *
 * Catalog: https://soultownmusic.com/api/radio/tracks (public, CORS open, 192kbps
 * MP3 with range support). Two <audio> elements alternate so every track change
 * is a 2 second crossfade instead of a gap. duck() pulls the music under the
 * companion's voice and brings it back when it is done talking.
 *
 * The station publishes track_id / artist_name / primary_genre, so load()
 * normalises every track to id / artist / genre once, here, and everything
 * downstream (the track list, the playlists, the corner player) reads those.
 *
 * Two lists, not one. `tracks` is the station catalog in published order, which
 * is what the music tab lists. `queue` is what is actually playing: the station,
 * or one of the player's playlists. Shuffle draws from a pool of indexes that
 * have not played yet, so nothing comes round again until the whole queue has
 * been heard. Repeat cycles off, all, one. Both settings live in localStorage,
 * so the player opens the way it was left.
 */

const CATALOG = 'https://soultownmusic.com/api/radio/tracks';
const FADE_MS = 2000;
const DUCK_LEVEL = 0.22;
const REPEATS = ['off', 'all', 'one'];
const STATION = 'The station';

// These belong to the device, not the account, and the settings document does
// not carry them. localStorage throws in some privacy modes, so it is guarded.
const readPref = (k, fallback) => { try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; } };
const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode, keep playing */ } };

/** One shape for a track no matter which field names the station used. */
function normalize(t) {
  const artist = t.artist_name || (typeof t.artist === 'string' ? t.artist : t.artist?.name) || '';
  return {
    ...t,
    id: t.id || t.track_id || t.audio_url,
    title: t.title || 'Untitled',
    artist,
    artistId: t.artist_id || null,
    genre: t.primary_genre || t.genre || '',
  };
}

export function createRadio({ onTrack, onState, onTime, onMode } = {}) {
  const decks = [new Audio(), new Audio()];
  decks.forEach((a) => { a.crossOrigin = 'anonymous'; a.preload = 'auto'; });
  let cur = 0;
  let tracks = [];            // the station catalog, published order
  let queue = [];             // what is playing now: the station or one playlist
  let queueName = STATION;
  let index = -1;             // position inside queue
  let pool = [];              // shuffle: queue indexes not played this cycle
  let volume = 0.8;
  let ducked = false;
  let playing = false;
  let fading = false;
  let ready = null;

  const savedRepeat = readPref('hb-repeat', 'all');
  let shuffle = readPref('hb-shuffle', 'off') === 'on';
  let repeat = REPEATS.includes(savedRepeat) ? savedRepeat : 'all';

  const deck = () => decks[cur];
  const other = () => decks[1 - cur];
  const target = () => (ducked ? volume * DUCK_LEVEL : volume);
  const modeState = () => ({ shuffle, repeat, queueName });

  function ramp(a, to, ms, done) {
    const from = a.volume;
    const start = performance.now();
    (function step() {
      const k = Math.min(1, (performance.now() - start) / ms);
      a.volume = from + (to - from) * k;
      if (k < 1) requestAnimationFrame(step); else done?.();
    })();
  }

  async function load() {
    if (ready) return ready;
    ready = fetch(CATALOG).then((r) => r.json()).then((d) => {
      tracks = (d.tracks || d).filter((t) => t && t.audio_url).map(normalize);
      queue = tracks; queueName = STATION; pool = [];
      return tracks;
    });
    return ready;
  }

  /* ------------------------------------------------------------- shuffle */

  function refillPool() {
    pool = [...queue.keys()];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    // pop() takes from the end, so keep the track that just played off the end
    // and a new cycle can never open on the song that closed the last one.
    if (pool.length > 1 && pool[pool.length - 1] === index) {
      [pool[0], pool[pool.length - 1]] = [pool[pool.length - 1], pool[0]];
    }
  }

  function takeFromPool() {
    if (!pool.length) refillPool();
    const i = pool.pop();
    return i === undefined ? -1 : i;
  }

  // Playing a track on purpose spends it, so the rest of the cycle stays honest.
  function usePool(i) { pool = pool.filter((n) => n !== i); }

  /** The index to play after this one, or -1 when the queue is finished. */
  function nextIndex() {
    if (!queue.length) return -1;
    if (repeat === 'one') return index < 0 ? 0 : index;
    if (shuffle) {
      if (!pool.length && repeat !== 'all') return -1;   // every track has been heard
      return takeFromPool();
    }
    const n = index + 1;
    if (n < queue.length) return n;
    return repeat === 'all' ? 0 : -1;
  }

  /* ----------------------------------------------------------- playback */

  /**
   * Retire a deck. The handlers come off BEFORE the source does, because
   * emptying src fires a media error (code 4, source not supported) and the
   * deck still carries the onerror of the track it just finished. That handler
   * reads the error as a dead track and calls next(), which retires the other
   * deck, which fires again: one crossfade turns into a run through the whole
   * catalog. Take the attribute off and load() instead of assigning '', so the
   * element lands in the empty state without an error at all.
   */
  function release(a) {
    a.onended = null; a.ontimeupdate = null; a.onerror = null;
    a.pause();
    a.removeAttribute('src');
    a.load();
  }

  function startOn(a, t, fadeIn) {
    a.src = t.audio_url;
    a.volume = fadeIn ? 0 : target();
    a.onended = null; a.ontimeupdate = null; a.onerror = null;
    a.onerror = () => { console.warn('radio: track failed, skipping', t.title); next().catch(() => {}); };
    a.ontimeupdate = () => {
      if (a === deck()) onTime?.(a.currentTime, a.duration || 0);
      if (!fading && a.duration && a.duration - a.currentTime <= FADE_MS / 1000 + 0.1) crossfade();
    };
    a.onended = () => { if (!fading) advance(); };
    const p = a.play();
    if (fadeIn) ramp(a, target(), FADE_MS);
    onTrack?.(t);
    return p;
  }

  function crossfade() {
    const n = nextIndex();
    if (n < 0) return;              // nothing after this one: let it play out
    fading = true;
    const from = deck(), to = other();
    index = n; usePool(n);
    startOn(to, queue[index], true);
    ramp(from, 0, FADE_MS, () => { release(from); fading = false; });
    cur = 1 - cur;
  }

  /** Jump straight to a position in the current queue. */
  async function goTo(i) {
    await load();
    if (!queue.length) throw new Error('SoulTown catalog is empty');
    fading = false;
    release(other());
    index = ((i % queue.length) + queue.length) % queue.length;
    usePool(index);
    await startOn(deck(), queue[index], true);
    playing = true; onState?.('playing');
  }

  /** What the deck does on its own when a track ends. */
  async function advance() {
    const n = nextIndex();
    if (n < 0) { stop(); return; }
    await goTo(n).catch(() => {});
  }

  function setQueue(list, name) {
    queue = list; queueName = name; index = -1; pool = [];
    onMode?.(modeState());
  }

  async function play() {
    await load();
    if (!queue.length) throw new Error('SoulTown catalog is empty');
    if (deck().src && deck().paused && index >= 0) {
      await deck().play(); playing = true; onState?.('playing'); return;
    }
    // First press of the session: open somewhere other than the top of the list.
    const start = index >= 0 ? index
      : (shuffle ? takeFromPool() : Math.floor(Math.random() * queue.length));
    await goTo(start < 0 ? 0 : start);
  }

  function pause() {
    decks.forEach((a) => a.pause());
    playing = false; onState?.('paused');
  }

  /** The skip button. Repeat one is about the deck looping, not about skip. */
  async function next() {
    await load();
    if (!queue.length) throw new Error('SoulTown catalog is empty');
    const n = repeat === 'one'
      ? (shuffle ? takeFromPool() : (index + 1) % queue.length)
      : nextIndex();
    return goTo(n < 0 ? 0 : n);     // asking for another track at the end wraps
  }

  /** Play the station catalog from a given row. */
  async function playAt(i) {
    await load();
    if (queue !== tracks) setQueue(tracks, STATION);
    return goTo(i);
  }

  /** Play a saved playlist. Tracks come from the player's document, so clean them. */
  async function playQueue(list, startIndex = 0, name = 'Your playlist') {
    await load();
    const clean = (list || []).filter((t) => t && t.audio_url).map(normalize);
    if (!clean.length) throw new Error('Nothing in there can be played yet');
    setQueue(clean, name);
    return goTo(startIndex);
  }

  function duck(on) {
    ducked = on;
    decks.forEach((a) => { if (a.src && !a.paused) ramp(a, target(), 350); });
  }

  function setVolume(v) {
    volume = Math.max(0, Math.min(1, v));
    decks.forEach((a) => { if (a.src && !a.paused && !fading) a.volume = target(); });
  }

  function setShuffle(on) {
    shuffle = !!on;
    writePref('hb-shuffle', shuffle ? 'on' : 'off');
    pool = [];
    if (shuffle) refillPool();
    onMode?.(modeState());
    return shuffle;
  }

  function setRepeat(mode) {
    repeat = REPEATS.includes(mode) ? mode : 'off';
    writePref('hb-repeat', repeat);
    onMode?.(modeState());
    return repeat;
  }

  function cycleRepeat() { return setRepeat(REPEATS[(REPEATS.indexOf(repeat) + 1) % REPEATS.length]); }

  function stop() { decks.forEach((a) => { a.pause(); a.currentTime = 0; }); playing = false; onState?.('stopped'); }
  function seek(sec) { const a = deck(); if (a.duration) a.currentTime = Math.max(0, Math.min(a.duration - 0.5, sec)); }
  function back(sec = 15) { seek(deck().currentTime - sec); }

  return {
    load, play, pause, stop, seek, back, playAt, playQueue, next, duck, setVolume,
    setShuffle, setRepeat, cycleRepeat,
    get position() { return deck().currentTime || 0; },
    get duration() { return deck().duration || 0; },
    get tracks() { return tracks; },
    get queue() { return queue; },
    get queueName() { return queueName; },
    get index() { return index; },
    // Always a promise. pause() returns nothing, and both players call this as
    // toggle().then(...).catch(...), so the bare value threw a TypeError on
    // every press of pause.
    toggle() { return playing ? Promise.resolve(pause()) : play(); },
    get playing() { return playing; },
    get volume() { return volume; },
    get shuffle() { return shuffle; },
    get repeat() { return repeat; },
    get modes() { return modeState(); },
    get current() { return index >= 0 ? queue[index] : null; },
    get count() { return tracks.length; },
  };
}
